import React, { useState } from 'react';
import { useFormik } from 'formik';

import styles from '../PauseContractLine/pause-contract-line-modal.module.scss';

import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { ResumeContractLineParams } from '../../hooks/useResumeContractLine';
import { initialValues, validationSchema } from '../ResumeBilling/validation';
import Spinner from '@/components/common/Spinner/Spinner';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import { ContractLineSelectionPayload } from 
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { groupSelectedLinesByContract } from '@/utils/contractLineHelpers';

interface IResumeContractLineModalProps {
  onClose: () => void;
  resumeContractLine: (
    inputParams: ResumeContractLineParams, 
    includeRegularLines?: boolean, 
    includeVariableLines?: boolean
  ) => void;
  selectedLines: Array<{ id: string; payload: ContractLineSelectionPayload }>;
  selectedVariableLines?: Array<{ id: string; payload: ContractLineSelectionPayload }>;
  isLoadingReasonCodes?: boolean;
  readOnly?: boolean;
}

export default function ResumeContractLineModal({
  onClose,
  resumeContractLine,
  selectedLines,
  selectedVariableLines = [],
  isLoadingReasonCodes,
  readOnly
}: IResumeContractLineModalProps) {
  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => undefined,
    validateOnMount: true
  });

  const onResumeContractLine = () => {
    // Combine all selected lines into one array
    const allLines = [...selectedLines, ...selectedVariableLines];
    const allContracts = groupSelectedLinesByContract(allLines);

    // Smart detection: determine which queries need refetching
    // - If only variable lines selected: refetch only variable query
    // - If only regular lines selected: refetch only regular query
    // - If both selected: refetch both queries
    const hasRegularLines = selectedLines.length > 0;
    const hasVariableLines = selectedVariableLines.length > 0;

    // Single mutation call with combined data
    resumeContractLine(
      {
        contracts: allContracts,
        comment: values.comment || ''
      },
      hasRegularLines,
      hasVariableLines
    );
  };

  const allSelectedLines = [...selectedLines, ...selectedVariableLines];
  const pausedLines = allSelectedLines.filter(line => {
    const contractLine = line.payload.contractLine;
    return 'isPaused' in contractLine ? contractLine.isPaused : false;
  });

  if (isWarningVisible) {
    return (
      <AgConfirmationModal
        isOpen={true}
        title="You are about to resume billing for selected contract lines"
        onClose={onClose}
        onConfirm={onResumeContractLine}
        contentStyle={{ width: 680 }}
        isConfirmButtonDisabled={isLoadingReasonCodes}
      >
        {isLoadingReasonCodes && (
          <div className="mt-50 mb-50 d-flex justify-content-center">
            <Spinner />
          </div>
        )}

        {!isLoadingReasonCodes && (
          <>
            <b>Please confirm if you want to proceed further and resume billing for the selected contract lines.</b>
            <br />
            <br />
            <strong>Selected contract lines: {selectedLines.length + selectedVariableLines.length}</strong>
            <br />
            <br />
            {pausedLines.length > 0 && (
              <div className={ styles['selected-lines-text']}>
                <strong>Paused lines to be resumed:</strong>
                {pausedLines.slice(0, 3).map((line) => {
                  const contractLine = line.payload.contractLine;
                  const stockCode = 'stockCode' in contractLine ? contractLine.stockCode : null;
                  const stockName = 'stockName' in contractLine ? contractLine.stockName : null;
                  return (
                    <div key={line.id}>
                      • {stockCode || 'N/A'} - {stockName || 'Unknown Product'}
                    </div>
                  );
                })}
                {pausedLines.length > 3 && (
                  <div>... and {pausedLines.length - 3} more</div>
                )}
              </div>
            )}
          </>
        )}
      </AgConfirmationModal>
    );
  }

  return (
    <AgModalWindow
      title={`Resume Billing - ${selectedLines.length + selectedVariableLines.length} 
      Contract Line${(selectedLines.length + selectedVariableLines.length) > 1 ? 's' : ''}`}
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: 340 }}
    >
      {readOnly && (
        <div
          style={{
            padding: '10px 15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#856404',
            marginBottom: '15px'
          }}
        >
          <strong>Note: </strong>Only users with Administrator or Full Access roles can edit information.
        </div>
      )}

      <div className="mb-15">
        <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <strong>Selected Contract Lines: {selectedLines.length + selectedVariableLines.length}</strong>
          <br />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {allSelectedLines.slice(0, 3).map((line) => {
              const contractLine = line.payload.contractLine;
              const stockCode = 'stockCode' in contractLine ? contractLine.stockCode : null;
              const stockName = 'stockName' in contractLine ? contractLine.stockName : null;
              return (
                <div key={line.id}>
                  {stockCode || 'N/A'} - {stockName || 'Unknown Product'}
                </div>
              );
            })}
            {allSelectedLines.length > 3 && (
              <div>... and {allSelectedLines.length - 3} more</div>
            )}
          </div>
        </div>
      </div>

      <ModalFormTextarea
        id="comment"
        labelTitle="Comment"
        placeholder="Enter short comments"
        value={values.comment ?? undefined}
        onChange={(event) => {
          setFieldTouched('comment', true, false);
          setFieldValue('comment', event.target.value);
        }}
        validationMessage={
          errors?.comment && touched.comment ? errors.comment : undefined
        }
      />

      <ActionButtonsWrapper
        leftButton={(
          <Button
            buttonStyleType={ButtonStyleType.SECONDARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        rightButton={(
          <Button
            disabled={!isValid || readOnly}
            buttonStyleType={ButtonStyleType.PRIMARY}
            onClick={() => setIsWarningVisible(true)}
          >
            Save
          </Button>
        )}
      />
    </AgModalWindow>
  );
}
